/**
 * scaling-policy.ts
 *
 * Global Scaling Policy defaults, observation window tracking,
 * and hysteresis-gated scale signal evaluation.
 */

import { ScalingPolicy } from '../contracts/model-orchestration-contracts.js';

export const DEFAULT_SCALING_POLICY: ScalingPolicy = {
  scaleUpUtilizationPercent: 80,
  scaleDownUtilizationPercent: 30,
  scaleUpWindowMs: 15000,
  scaleDownWindowMs: 60000,
  cooldownMs: 30000,
  maxScaleStep: 1,
};

export interface UtilizationSample {
  readonly timestamp: number;
  readonly utilizationPercent: number;
}

export class ScalingTracker {
  private samples = new Map<string, UtilizationSample[]>(); // `${nodeId}:${modelId}` -> samples
  private lastSignalTime = new Map<string, number>(); // `${nodeId}:${modelId}` -> last emitted ms

  private getKey(nodeId: string, modelId: string): string {
    return `${nodeId}:${modelId}`;
  }

  recordUtilization(nodeId: string, modelId: string, utilizationPercent: number, now: number = Date.now()): void {
    const key = this.getKey(nodeId, modelId);
    const list = this.samples.get(key) || [];
    list.push({ timestamp: now, utilizationPercent });

    // Prune samples older than 2 minutes
    const pruned = list.filter((s) => now - s.timestamp <= 120000);
    this.samples.set(key, pruned);
  }

  isCooldownActive(nodeId: string, modelId: string, policy: ScalingPolicy, now: number = Date.now()): boolean {
    const key = this.getKey(nodeId, modelId);
    const last = this.lastSignalTime.get(key);
    if (!last) return false;
    return now - last < policy.cooldownMs;
  }

  recordSignalEmitted(nodeId: string, modelId: string, now: number = Date.now()): void {
    const key = this.getKey(nodeId, modelId);
    this.lastSignalTime.set(key, now);
  }

  evaluateScaleSignal(
    nodeId: string,
    modelId: string,
    policy: ScalingPolicy = DEFAULT_SCALING_POLICY,
    now: number = Date.now()
  ): 'SCALE_UP_RECOMMENDED' | 'SCALE_DOWN_RECOMMENDED' | null {
    if (this.isCooldownActive(nodeId, modelId, policy, now)) {
      return null;
    }

    const key = this.getKey(nodeId, modelId);
    const list = this.samples.get(key) || [];
    if (list.length === 0) return null;

    // Evaluate Scale Up: window of scaleUpWindowMs
    const scaleUpWindow = list.filter((s) => now - s.timestamp <= policy.scaleUpWindowMs);
    if (scaleUpWindow.length > 0) {
      const oldestInWindow = scaleUpWindow[0].timestamp;
      // Window must span at least 50% of scaleUpWindowMs to be sustained
      if (now - oldestInWindow >= policy.scaleUpWindowMs * 0.5) {
        const avgUtil = scaleUpWindow.reduce((sum, s) => sum + s.utilizationPercent, 0) / scaleUpWindow.length;
        if (avgUtil >= policy.scaleUpUtilizationPercent) {
          return 'SCALE_UP_RECOMMENDED';
        }
      }
    }

    // Evaluate Scale Down: window of scaleDownWindowMs
    const scaleDownWindow = list.filter((s) => now - s.timestamp <= policy.scaleDownWindowMs);
    if (scaleDownWindow.length > 0) {
      const oldestInWindow = scaleDownWindow[0].timestamp;
      if (now - oldestInWindow >= policy.scaleDownWindowMs * 0.5) {
        const avgUtil = scaleDownWindow.reduce((sum, s) => sum + s.utilizationPercent, 0) / scaleDownWindow.length;
        if (avgUtil <= policy.scaleDownUtilizationPercent) {
          return 'SCALE_DOWN_RECOMMENDED';
        }
      }
    }

    return null;
  }

  clear(): void {
    this.samples.clear();
    this.lastSignalTime.clear();
  }
}
